// "Edit itinerary" modal of a charter booking: origin, destination, departure date and time.
import type { FormInstance } from 'antd';
import { DatePicker, Form, Modal, Space, TimePicker, Typography } from 'antd';

import { AirportSelect } from '../../components/AirportSelect';
import { SameCityNotice } from '../../components/SameCityNotice';
import type { CharterBookingUpdate } from './charterBookingUpdate';
import type { AirportHit } from '../../api/types';

const { Text } = Typography;

export function ItineraryModal({
  itineraryForm,
  itineraryOpen,
  setItineraryOpen,
  submitItinerary,
  submitGuard,
  saveMut,
  onOriginAirport,
  departureClock,
}: {
  itineraryForm: FormInstance;
  itineraryOpen: boolean;
  setItineraryOpen: (open: boolean) => void;
  submitItinerary: () => void;
  submitGuard: { busy: boolean };
  saveMut: { isPending: boolean; mutate: (body: CharterBookingUpdate) => void };
  /** SHAR-1192 — the airport picked as origin, whose clock the departure is read on. */
  onOriginAirport?: (a: AirportHit | null) => void;
  /** SHAR-1192 — whose clock the departure fields are on. */
  departureClock?: string;
}) {
  return (
    <Modal
      title="Edit itinerary"
      open={itineraryOpen}
      onCancel={() => setItineraryOpen(false)}
      onOk={submitItinerary}
      confirmLoading={submitGuard.busy || saveMut.isPending}
      okText="Save"
      destroyOnClose
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        These values come from the quote this booking was made from. A change here is recorded on
        the booking; the quote the customer accepted is not changed. Arrival follows the departure
        by the quoted flight time.
      </Text>
      {/* SHAR-758 — the same notice the ShareJet itinerary editor shows. The
          AC asks for parity with that screen, and there the notice plus a
          server-side refusal IS the implementation: the typeahead searches
          every airport, and an out-of-city choice is rejected on save. Saying
          it up front is what stops the admin discovering the rule from an
          error message. */}
      <SameCityNotice />
      <Form form={itineraryForm} layout="vertical">
        <Space size="middle" style={{ display: 'flex', alignItems: 'flex-start' }}>
          <Form.Item
            name="originCode"
            label="Origin airport"
            rules={[{ required: true, message: 'Required' }]}
            style={{ flex: 1 }}
          >
            <AirportSelect onSelectAirport={onOriginAirport} />
          </Form.Item>
          <Form.Item
            name="destCode"
            label="Destination airport"
            rules={[{ required: true, message: 'Required' }]}
            style={{ flex: 1 }}
          >
            <AirportSelect />
          </Form.Item>
        </Space>
        <Space size="middle" style={{ display: 'flex' }}>
          <Form.Item
            name="departureDate"
            label="Departure date"
            tooltip={departureClock ? `On the origin airport's clock: ${departureClock}` : undefined}
            rules={[{ required: true, message: 'Required' }]}
            style={{ flex: 1 }}
          >
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="departureTime"
            label="Departure time"
            tooltip={departureClock ? `On the origin airport's clock: ${departureClock}` : undefined}
            rules={[{ required: true, message: 'Required' }]}
            style={{ flex: 1 }}
          >
            <TimePicker format="HH:mm" minuteStep={5} style={{ width: '100%' }} />
          </Form.Item>
        </Space>
      </Form>
    </Modal>
  );
}
